import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type FeeCalculationMethod = 'FLAT' | 'PER_UNIT' | 'PERCENTAGE';

export interface FeeRateWindow {
  id: string;
  amount: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

export interface FeeScopeRate {
  current: FeeRateWindow | null;
  /** `amount: null` = falls back to the platform default (business) or the business rate (merchant). */
  next: { amount: string | null; effectiveFrom: string } | null;
}

export interface TenantFee {
  id: string;
  code: string;
  name: string;
  description: string | null;
  calculationMethod: FeeCalculationMethod;
  statementType: string;
  defaultAmount: string;
  tenant: FeeScopeRate;
  merchants: (FeeScopeRate & { merchantId: string })[];
}

export interface TenantFeeHistoryRow {
  id: string;
  feeCode: string;
  feeName: string;
  calculationMethod: FeeCalculationMethod;
  merchantId: string | null;
  amount: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt: string;
  createdBy: string | null;
}

export interface TenantFeeChange {
  feeTypeId: string;
  merchantId: string | null;
  /** null resets to the fallback rate. */
  amount: string | null;
}

const feesKey = (tenantId: string) => ['admin', 'tenant-fees', tenantId] as const;

export function useAdminTenantFees(tenantId: string) {
  return useQuery({
    queryKey: feesKey(tenantId),
    queryFn: () => api.get<{ data: { asOf: string; fees: TenantFee[] } }>(`/admin/tenants/${tenantId}/fees`),
    enabled: !!tenantId,
  });
}

export function useAdminTenantFeeHistory(tenantId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...feesKey(tenantId), 'history'],
    queryFn: () => api.get<{ data: TenantFeeHistoryRow[] }>(`/admin/tenants/${tenantId}/fees/history`),
    enabled: !!tenantId && enabled,
  });
}

export function useSaveTenantFeeChanges(tenantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { effectiveFrom: string; changes: TenantFeeChange[] }) =>
      api.post<{ data: { saved: number; effectiveFrom: string } }>(`/admin/tenants/${tenantId}/fees/changes`, body),
    onSuccess: async () => {
      // Prefix match: refreshes the rate card and the history list.
      await queryClient.invalidateQueries({ queryKey: feesKey(tenantId) });
    },
  });
}
